import { createContext, useContext } from "react";
import type { AppState, ScanLog, SavedContact, User } from "../types";
import type { EventRolePayload } from "../services/visitorSessionService";

export interface AppStore extends AppState {
  selectEvent: (eventId: string) => void;
  completeOnboarding: () => void;
  addScan: (scan: ScanLog) => void;
  /** Patch notes/tags on an existing scan row in local history. */
  updateScan: (scanId: string, patch: Pick<ScanLog, "notes" | "tags">) => void;
  saveContact: (contact: SavedContact) => void;
  /** Remove a saved company contact from local favorites. */
  removeSavedContact: (companyId: string, eventId?: string) => void;
  /** Merge notes/tags onto an existing saved contact (or create). */
  updateSavedContact: (
    companyId: string,
    patch: Pick<SavedContact, "notes" | "tags"> & Partial<Pick<SavedContact, "eventId" | "userId">>,
  ) => void;
  isContactUnlocked: (companyId: string) => boolean;
  /** Personal rep contacts unlock only after badge QR/barcode scan. */
  isRepresentativeUnlocked: (representativeId: string) => boolean;
  updateUser: (user: Partial<User>) => void;
  setEventRole: (role: EventRolePayload | null) => void;
  reset: () => void;
  navbarVisible: boolean;
  setNavbarVisible: (visible: boolean) => void;
}

export const AppStoreContext = createContext<AppStore | null>(null);

export function useAppStore(): AppStore {
  const ctx = useContext(AppStoreContext);
  if (!ctx) throw new Error("useAppStore must be used within AppStoreProvider");
  return ctx;
}

/** For shared UI used in both client and admin bundles (no provider in admin). */
export function useOptionalAppStore(): AppStore | null {
  return useContext(AppStoreContext);
}
